/**
 * URL's herkennen in vrije tekst, zodat een medewerker een briefing-link kan openen (app#217).
 *
 * Een planner zet de link gewoon in de beschrijving van een opdracht. Die tekst komt van een klant
 * en gaat dus nooit als HTML het scherm op: deze functie hakt hem in stukken en de component
 * eromheen rendert elk stuk als tekst of als `<a>`. Geen `dangerouslySetInnerHTML`.
 *
 * Een link wordt het alleen als er `http://` of `https://` staat, of als er een host staat die met
 * `www.` begint. Dat is dezelfde grens die `openExternalUrl()` aanhoudt, en hij sluit
 * `javascript:`, `data:` en `file:` per constructie uit: die staan niet in het patroon en komen ook
 * niet door de protocolcontrole erachter. De echte bewaker blijft `openExternalUrl()`; deze
 * controle staat ervoor zodat er nooit een `href` op het scherm komt die daar toch op afketst.
 */

export type LinkifySegment =
  | { type: "text"; value: string }
  | { type: "link"; value: string; href: string };

/** De link-stukken apart, want de tekst en de href lopen niet altijd gelijk. */
export type LinkifyLink = Extract<LinkifySegment, { type: "link" }>;

/**
 * Twee vormen, in één pass zodat ze in de goede volgorde uit de tekst komen.
 *
 * De eerste tak is een URL met schema: alles tot aan de eerste witruimte. `<`, `>` en de rechte
 * aanhalingstekens sluiten aan omdat ze in de praktijk om een URL heen staan en er nooit onontsnapt
 * in horen. Wat er verder aan leestekens achter plakt haalt `trimTrailing()` eraf.
 *
 * De tweede tak is een host die met `www.` begint, en die is bewust streng: nul of meer labels en
 * dan een topleveldomein van minstens twee letters, eventueel gevolgd door een pad. Zonder die
 * strengheid wordt van elke zin met een punt erin een link.
 *
 * Volgorde telt niet voor de voorrang maar voor de positie: een regex loopt van links naar rechts,
 * dus `https://www.voorbeeld.nl` wordt in zijn geheel door de eerste tak gepakt en de `www` erin
 * komt nooit los aan bod.
 */
const LINK_PATTERN =
  /(https?:\/\/[^\s<>"']+)|(www\.(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)*[a-z]{2,}(?:[/?#][^\s<>"']*)?)/gi;

/**
 * Tekens die nooit aan het eind van een URL horen, maar er in een zin wel achter komen te staan.
 *
 * De typografische aanhalingstekens staan er niet voor de sier. iOS en macOS zetten slimme
 * aanhalingstekens standaard aan, dus een planner die zijn link tussen aanhalingstekens zet typt
 * geen `"` maar `”`, en die viel voorheen binnen de URL: de href eindigde op `%E2%80%9D` en leverde
 * een 404 op. Precies de fout die dit bestand hoort op te lossen.
 */
const TRAILING_PUNCTUATION = new Set([
  ".",
  ",",
  ";",
  ":",
  "!",
  "?",
  "…",
  "»",
  "“",
  "”",
  "‘",
  "’",
  "„",
  "‚",
  "`",
  "´",
]);

const CLOSING_BRACKETS: Record<string, string | undefined> = { ")": "(", "]": "[", "}": "{" };

/**
 * Wat er vlak vóór een link mag staan, en dat verschilt per vorm.
 *
 * `https://` is een ondubbelzinnig signaal: alleen een woordteken ervoor betekent dat het geen
 * losse URL is, want anders wordt van `xhttps://voorbeeld.nl` het stuk vanaf de `h` een link die
 * ergens anders heen wijst dan wat er staat.
 *
 * `www.` is een veel zwakker signaal en krijgt daarom een strengere grens: ook een punt, een
 * schuine streep of een streepje ervoor betekent dat het bij het woord ervoor hoort. Dat is wat
 * `bel.www.nu` tegenhoudt.
 *
 * Geen lookbehind in het patroon zelf: die kwam pas in iOS Safari 16.4 en de webview van de app is
 * diezelfde engine.
 */
const BREAKS_SCHEME = /[\w@]/;
const BREAKS_HOST = /[\w@./-]/;

function countOccurrences(text: string, character: string): number {
  let total = 0;
  for (const current of text) {
    if (current === character) {
      total++;
    }
  }
  return total;
}

/**
 * De leestekens achter een URL eraf halen, zonder de URL zelf te beschadigen.
 *
 * Een punt aan het eind van een zin hoort er niet bij. Een haakje wel, zolang het er een sluit dat
 * in de URL zelf openging: `.../pad_(uitleg)` blijft heel, maar `(zie https://voorbeeld.nl)` niet.
 */
function trimTrailing(url: string): string {
  let end = url.length;

  while (end > 0) {
    const character = url[end - 1];

    if (TRAILING_PUNCTUATION.has(character)) {
      end--;
      continue;
    }

    const opening = CLOSING_BRACKETS[character];
    if (opening) {
      const head = url.slice(0, end);
      if (countOccurrences(head, character) > countOccurrences(head, opening)) {
        end--;
        continue;
      }
    }

    break;
  }

  return url.slice(0, end);
}

function isHttpUrl(candidate: string): boolean {
  try {
    const { protocol } = new URL(candidate);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * De tekst opdelen in stukken tekst en stukken link, in de volgorde waarin ze staan.
 *
 * Alles wat geen link wordt komt ongewijzigd als tekst terug, inclusief de witruimte. Zo blijft
 * `whitespace-pre-wrap` bij de aanroeper doen wat het deed.
 *
 * Bij een `www.`-link verschillen `value` en `href` met opzet: op het scherm blijft staan wat de
 * planner typte, de href krijgt er `https://` voor omdat een href zonder schema als relatief pad
 * wordt gelezen en binnen de app blijft. Misleidend is dat niet, want de host in de href is exact
 * de tekst die er staat; er komt alleen het schema bij dat een browser er zelf ook voor zet als je
 * `www.iets.nl` in de adresbalk typt. Https en niet http, want dat is de veilige van de twee en
 * elke server die beide spreekt kiest hem ook.
 *
 * De reden dat dit erbij moest: van de 98 beschrijvingen met een link in productie stonden er 52
 * zonder schema, en bij Jongens van de Bar 50 van de 51.
 */
export function linkify(text: string): LinkifySegment[] {
  const segments: LinkifySegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0;
    const [matched, withScheme] = match;

    const breaks = withScheme ? BREAKS_SCHEME : BREAKS_HOST;
    if (start > 0 && breaks.test(text[start - 1])) {
      continue;
    }

    const value = trimTrailing(matched);
    const href = withScheme ? value : `https://${value}`;
    if (!isHttpUrl(href)) {
      continue;
    }

    if (start > cursor) {
      segments.push({ type: "text", value: text.slice(cursor, start) });
    }
    segments.push({ type: "link", value, href });
    cursor = start + value.length;
  }

  if (cursor < text.length) {
    segments.push({ type: "text", value: text.slice(cursor) });
  }

  return segments;
}

/**
 * Hetzelfde, maar voor tekst die is afgekapt.
 *
 * Op het projectscherm staat een preview van 150 tekens met "Meer informatie" eronder. Valt die
 * knip midden in een URL, dan levert `linkify()` een link op die naar een half adres wijst.
 * Misleidend is dat niet, want de href loopt gelijk met wat er staat, maar de medewerker tikt erop
 * en komt op een 404. En het is geen randgeval: vestigingbeschrijvingen zijn op productie gemiddeld
 * 177 tekens, dus er wordt vaak net voorbij de grens geknipt.
 *
 * Alleen het laatste segment kan doorgesneden zijn, want alles daarvoor is per definitie afgesloten
 * door de tekst erachter. Is dat laatste segment een link, dan gaat hij hier als gewone tekst het
 * scherm op. Links die eerder in de preview volledig staan blijven gewoon klikbaar, en de hele
 * beschrijving staat één tik verderop in de modal, waar hij ongeknipt door `linkify()` gaat.
 *
 * Een URL die toevallig precies op de knip eindigt verliest zo zijn link. Dat is de veilige kant om
 * op te vallen: zonder de volledige tekst valt niet te zien of er nog iets achteraan kwam.
 */
export function linkifyTruncated(text: string): LinkifySegment[] {
  const segments = linkify(text);
  const last = segments.at(-1);

  if (last?.type !== "link") {
    return segments;
  }

  const head = segments.slice(0, -1);
  const previous = head.at(-1);

  // Aan de vorige tekst plakken in plaats van er een tweede tekstsegment naast te zetten, zodat
  // dezelfde tekst altijd dezelfde segmenten oplevert.
  return previous?.type === "text"
    ? [...head.slice(0, -1), { type: "text", value: previous.value + last.value }]
    : [...head, { type: "text", value: last.value }];
}
